import { VERTEX_SRC, FRAGMENT_SRC } from './shaders.js'

/**
 * WebGL renderer for the home-page hero gallery.
 *
 * Ported & cleaned from the designer prototype (Code_Example/script.js). Class
 * shape mirrors src/image-styler/engine.js / src/page-bg/engine.js for
 * consistency: `new HomeGalleryEngine(canvas)`, `setPalette()`, `await setImages()`,
 * `render(timeSec)`, `resize()`, `dispose()`.
 *
 * One WebGL context with THREE texture slots (front / back / next) rotated on
 * `advance()` — image count never multiplies contexts. The slide cadence/index
 * is driven by the caller (Swiper, in src/home.js); the scroll-timeline value and
 * cursor are pushed via `setScroll()` / `setMouse()`. The keyed colour palette
 * comes from a single `Bildstil` preset (`setPalette`).
 */

const DPR_MAX = 2
const FADE_SECONDS = 0.4 // morph (front→back, next→front) duration

// Re-analysis throttle (histogram thresholds shift as the scroll tail grows).
const REANALYZE_DELTA = 0.01
const REANALYZE_MIN_DT = 0.05

// Dark/light image classification.
const DARK_MEAN_CUT = 0.45
const DARK_FRAC_CUT = 0.6

// Scroll timeline (caller feeds a smoothed scrollT in 0..T_END).
export const T_END = 1.85
const T_START = 0.0
const T_MID = 1.0
const T_TAKEOVER_END = 1.6

// BASE (scroll=0) → SCROLLED (scroll=1) parameter ramp.
// Values match the designer's landing-V3 prototype (palette_01..05.js); same for
// every palette, so they live here rather than per-palette.
const BASE = {
  speed: 0.2, scale: 15.0, influence: 7.0, softness: 0.055, mouseRadius: 0.3,
  noiseDetail: 0.7, noiseContrast: 1.8, noiseRidge: 1.0,
  lightPct: 0.0001, darkPct: 0.999,
}
const SCROLLED = {
  speed: 0.2, scale: 15.0, influence: 8.0, softness: 0.2, mouseRadius: 0.3,
  noiseDetail: 0.6, noiseContrast: 3.2, noiseRidge: 1.0,
  lightPct: 1.0, darkPct: 0.0,
}

const DEFAULT_PALETTE = { c0: '#00ccb3', c1: '#cac7b3', c2: '#ff3217', c3: '#ff6be4', c4: '#eeeeee' }

// Colour the scroll end-state floods to (see setFadeTarget). These MIRROR the page
// background — `--background-color` in src/sass/settings.sass (#FCFCFC), swapped to
// --black under `.dark-mode` (20_global.sass) — so the flooded canvas is
// indistinguishable from the page behind it. Keep them in sync with that variable.
// Light is the default; home.js switches to the dark target under `body.dark-mode`.
export const FADE_TO_LIGHT = '#fcfcfc'
export const FADE_TO_DARK = '#000000'

export class HomeGalleryEngine {
  constructor(canvas) {
    this.canvas = canvas
    this.gl = canvas.getContext('webgl', { premultipliedAlpha: false, antialias: false })
    if (!this.gl) throw new Error('WebGL not supported')

    this.maxSquare = Math.min(2048, this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE))
    this.maxDpr = DPR_MAX // home.js lowers this to cut fullscreen fragment cost
    this._lost = false
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this._lost = true }, false)
    this._maxRb = this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE) || 4096

    // run state
    this.time = 0
    this.keyAmount = 1 // 1 = keyed colour bands; home.js sets 0 for the reduced-motion (no-shader) gallery
    this.scrollT_s = 0
    this.mouse = { x: 0.5, y: 0.5 }
    this.CUR = { ...BASE }
    this._lastReanalyzeScroll = -1
    this._lastReanalyzeTime = -999

    // gallery state
    this.squareCache = [] // cropped square <canvas> per source image
    this.imageCount = 0
    this.loadedImgs = [null, null, null] // canvas currently in each texture slot
    this.slotHist = [null, null, null]   // cached per-slot histogram (scroll-independent)
    this.imgParams = [
      { t1: 0.01, t2: 0.02, t3: 0.03, mode: 0 },
      { t1: 0.01, t2: 0.02, t3: 0.03, mode: 0 },
      { t1: 0.01, t2: 0.02, t3: 0.03, mode: 0 },
    ]
    this.roles = { front: 0, back: 1, next: 2 }
    this.nextSrcIdx = 3
    this.transition = { active: false, startTime: 0, morph: 0 }
    this._pendingSwap = false

    this._setupProgram()
    this._setupGeometry()
    this._setupTextures()
    this._resolveUniforms()
    this.setPalette(DEFAULT_PALETTE)
    this.setFadeTarget(FADE_TO_LIGHT)
  }

  // ---- setup ----

  _compileShader(type, src) {
    const gl = this.gl
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s))
    return s
  }

  _setupProgram() {
    const gl = this.gl
    const p = gl.createProgram()
    gl.attachShader(p, this._compileShader(gl.VERTEX_SHADER, VERTEX_SRC))
    gl.attachShader(p, this._compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SRC))
    gl.linkProgram(p)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p))
    this.program = p
    gl.useProgram(p)
  }

  _setupGeometry() {
    const gl = this.gl
    this.buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const aPos = gl.getAttribLocation(this.program, 'a_pos')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)
  }

  _setupTextures() {
    const gl = this.gl
    this.tex = [0, 1, 2].map((unit) => {
      const t = gl.createTexture()
      gl.activeTexture(gl.TEXTURE0 + unit)
      gl.bindTexture(gl.TEXTURE_2D, t)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]))
      return t
    })
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_tex0'), 0)
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_tex1'), 1)
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_tex2'), 2)
  }

  _resolveUniforms() {
    const u = (n) => this.gl.getUniformLocation(this.program, n)
    this.u = {
      res: u('u_res'), frontIdx: u('u_frontIdx'), backIdx: u('u_backIdx'), nextIdx: u('u_nextIdx'),
      morph: u('u_morph'), time: u('uTime'), mouse: u('uMouse'),
      scale: u('uScale'), speed: u('uSpeed'), influence: u('uInfluence'),
      takeover: u('uTakeover'), whiteFade: u('uWhiteFade'), keyAmount: u('u_keyAmount'),
      softness: u('uSoftness'), mouseRadius: u('uMouseRadius'),
      noiseDetail: u('uNoiseDetail'), noiseContrast: u('uNoiseContrast'), noiseRidge: u('uNoiseRidge'),
      tail0: u('uTail0'), tail1: u('uTail1'), tail2: u('uTail2'),
      mode0: u('uMode0'), mode1: u('uMode1'), mode2: u('uMode2'),
      c0: u('uC0'), c1: u('uC1'), c2: u('uC2'), c3: u('uC3'), c4: u('uC4'),
      fadeTo: u('uFadeTo'),
    }
  }

  // ---- public API ----

  /** Set the keyed palette (c0..c4 hex) from a Bildstil preset. */
  setPalette(preset) {
    const gl = this.gl
    const p = { ...DEFAULT_PALETTE, ...(preset || {}) }
    gl.useProgram(this.program)
    gl.uniform3fv(this.u.c0, hexToRGB01(p.c0))
    gl.uniform3fv(this.u.c1, hexToRGB01(p.c1))
    gl.uniform3fv(this.u.c2, hexToRGB01(p.c2))
    gl.uniform3fv(this.u.c3, hexToRGB01(p.c3))
    gl.uniform3fv(this.u.c4, hexToRGB01(p.c4))
  }

  /**
   * Set the colour the scroll end-state floods to (`uWhiteFade` → 1). The whole frame
   * washes to it, so at full scroll the hero is a flat plate with no trace of the
   * photo left — it must therefore be the page background: light in light mode, black
   * in dark mode (home.js keeps it in sync with `body.dark-mode`). Safe to call at any
   * time — the next draw picks it up.
   */
  setFadeTarget(hex) {
    this.gl.useProgram(this.program)
    this.gl.uniform3fv(this.u.fadeTo, hexToRGB01(hex))
  }

  /**
   * Preload + center-crop every source to a square canvas, then seed the three
   * texture slots. `urls` is an array of image URLs (same-origin WP media).
   */
  async setImages(urls) {
    const loaded = await Promise.allSettled((urls || []).map(loadImage))
    const cache = []
    loaded.forEach((r) => { if (r.status === 'fulfilled') cache.push(cropImageToSquareCanvas(r.value, this.maxSquare)) })
    if (!cache.length) throw new Error('no home gallery images loaded')

    this.squareCache = cache
    this.imageCount = cache.length
    for (let slot = 0; slot < 3; slot++) {
      this._uploadCanvasToSlot(cache[slot % this.imageCount], slot)
    }
    this.roles = { front: 0, back: 1, next: 2 }
    this.nextSrcIdx = 3 // only consulted when imageCount > 3 (see render swap)
    // The slide-swap path (render) reads squareCache only when imageCount > 3, so
    // for <=3 images the cache is dead after the initial uploads — release it (each
    // cached square canvas is up to maxSquare² RGBA, i.e. tens of MB).
    if (this.imageCount <= 3) this.squareCache = []
    return this.imageCount
  }

  setScroll(t) { this.scrollT_s = clamp(t, 0, T_END) }

  setMouse(x, y) { this.mouse.x = x; this.mouse.y = y }

  /** Begin a front→back→next morph (ignored while one is already running). */
  advance() {
    if (this.transition.active || this.imageCount === 0) return
    this.transition.active = true
    this.transition.startTime = this.time
    this.transition.morph = 0
  }

  /**
   * Perceived brightness (0..1 mean luma) of the image sitting behind the fixed
   * header logo, or null. The logo is in the top-left CORNER, which falls in the
   * side band over the full-bleed BACK layer — NOT the centered front square —
   * so this reads roles.back. At rest the hero shows ~the raw photo, so mean luma
   * faithfully proxies the logo backdrop. See buildHistogram() + shaders.js.
   */
  logoBackdropMean() { return this.slotHist[this.roles.back]?.mean ?? null }

  /**
   * …of the image the logo backdrop morphs TO during an advance(): the front
   * square expands to full-bleed and becomes the next back layer, so read
   * roles.front (pre-swap). Emit at advance() time so the logo colour crossfades
   * in sync with the slide morph instead of a beat late.
   */
  logoIncomingMean() { return this.slotHist[this.roles.front]?.mean ?? null }

  resize() {
    const c = this.canvas
    if (typeof c.clientWidth !== 'number') return
    const dpr = Math.max(1, Math.min(this.maxDpr, (typeof devicePixelRatio !== 'undefined' ? devicePixelRatio : 1) || 1))
    let w = Math.floor(c.clientWidth * dpr)
    let h = Math.floor(c.clientHeight * dpr)
    const m = Math.max(w, h) // clamp to the GPU renderbuffer limit (old mobile GPUs: 2048–4096)
    if (m > this._maxRb) { const k = this._maxRb / m; w = Math.max(1, Math.floor(w * k)); h = Math.max(1, Math.floor(h * k)) }
    if (w > 0 && h > 0 && (c.width !== w || c.height !== h)) {
      c.width = w
      c.height = h
      this.gl.viewport(0, 0, w, h)
    }
  }

  render(timeSec) {
    if (this._lost) return // GPU context lost → skip until restored (caller shows fallback)
    this.time = timeSec
    this.resize()
    const gl = this.gl
    const c = this.canvas
    const u = this.u

    // BASE→SCROLLED parameter ramp (T_START..T_MID).
    const tParams = clamp01((this.scrollT_s - T_START) / (T_MID - T_START))
    smoothParams(this.CUR, BASE, SCROLLED, tParams)

    // Recompute per-image thresholds as the scroll tail changes (throttled).
    if (
      Math.abs(tParams - this._lastReanalyzeScroll) > REANALYZE_DELTA &&
      timeSec - this._lastReanalyzeTime > REANALYZE_MIN_DT
    ) {
      this._lastReanalyzeScroll = tParams
      this._lastReanalyzeTime = timeSec
      this._analyzeAll()
    }

    // Click/autoplay morph (0..1).
    let morph = 0
    if (this.transition.active) {
      morph = (timeSec - this.transition.startTime) / FADE_SECONDS
      if (morph >= 1) {
        morph = 1
        this.transition.active = false
        this._pendingSwap = true // swap roles AFTER drawing the morph=1 frame
      }
    }
    this.transition.morph = morph

    const tTakeover = clamp01((this.scrollT_s - T_START) / (T_TAKEOVER_END - T_START))
    const tFade = smoothstep(T_MID, T_END, this.scrollT_s)
    const s = this.CUR

    gl.useProgram(this.program)
    gl.uniform2f(u.res, c.width, c.height)
    gl.uniform1i(u.frontIdx, this.roles.front)
    gl.uniform1i(u.backIdx, this.roles.back)
    gl.uniform1i(u.nextIdx, this.roles.next)
    gl.uniform1f(u.morph, this.transition.morph)
    gl.uniform1f(u.time, timeSec)
    gl.uniform2f(u.mouse, this.mouse.x, this.mouse.y)
    gl.uniform1f(u.scale, s.scale)
    gl.uniform1f(u.speed, s.speed)
    gl.uniform1f(u.influence, s.influence)
    gl.uniform1f(u.softness, s.softness)
    gl.uniform1f(u.mouseRadius, s.mouseRadius)
    gl.uniform1f(u.noiseDetail, s.noiseDetail)
    gl.uniform1f(u.noiseContrast, s.noiseContrast)
    gl.uniform1f(u.noiseRidge, s.noiseRidge)
    gl.uniform1f(u.takeover, tTakeover)
    gl.uniform1f(u.whiteFade, tFade)
    gl.uniform1f(u.keyAmount, this.keyAmount)
    gl.uniform3f(u.tail0, this.imgParams[0].t1, this.imgParams[0].t2, this.imgParams[0].t3)
    gl.uniform3f(u.tail1, this.imgParams[1].t1, this.imgParams[1].t2, this.imgParams[1].t3)
    gl.uniform3f(u.tail2, this.imgParams[2].t1, this.imgParams[2].t2, this.imgParams[2].t3)
    gl.uniform1i(u.mode0, this.imgParams[0].mode | 0)
    gl.uniform1i(u.mode1, this.imgParams[1].mode | 0)
    gl.uniform1i(u.mode2, this.imgParams[2].mode | 0)

    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.tex[0])
    gl.activeTexture(gl.TEXTURE0 + 1); gl.bindTexture(gl.TEXTURE_2D, this.tex[1])
    gl.activeTexture(gl.TEXTURE0 + 2); gl.bindTexture(gl.TEXTURE_2D, this.tex[2])

    gl.drawArrays(gl.TRIANGLES, 0, 6)

    // Rotate roles + feed the next image AFTER drawing the morph=1 frame.
    if (this._pendingSwap) {
      this._pendingSwap = false
      this._rotateRoles()
      this.transition.morph = 0
      if (this.imageCount > 3) {
        const slot = this.roles.next
        const i = this.nextSrcIdx % this.imageCount
        this.nextSrcIdx++
        this._uploadCanvasToSlot(this.squareCache[i], slot)
      }
    }
  }

  dispose() {
    const gl = this.gl
    gl.deleteProgram(this.program)
    gl.deleteBuffer(this.buffer)
    this.tex.forEach((t) => gl.deleteTexture(t))
    this.squareCache = []
    this.loadedImgs = [null, null, null]
    this.slotHist = [null, null, null]
    gl.getExtension('WEBGL_lose_context')?.loseContext()
  }

  // ---- internals ----

  _rotateRoles() {
    const { front, back, next } = this.roles
    this.roles.front = next
    this.roles.back = front
    this.roles.next = back
  }

  // Re-derive thresholds for all slots from their CACHED histograms (cheap percentile
  // walks only). The expensive getImageData+65k-loop ran once per slot at upload.
  _analyzeAll() {
    for (let slot = 0; slot < 3; slot++) {
      if (this.slotHist[slot]) this.imgParams[slot] = deriveThresholds(this.slotHist[slot], this.CUR)
    }
  }

  _uploadCanvasToSlot(canvas, slot) {
    const gl = this.gl
    this.loadedImgs[slot] = canvas
    // Build the (scroll-independent) histogram ONCE here, then derive thresholds cheaply.
    this.slotHist[slot] = buildHistogram(canvas)
    this.imgParams[slot] = deriveThresholds(this.slotHist[slot], this.CUR)
    gl.activeTexture(gl.TEXTURE0 + slot)
    gl.bindTexture(gl.TEXTURE_2D, this.tex[slot])
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas)
  }
}

// ---- pure helpers ----

function clamp(x, a, b) { return Math.max(a, Math.min(b, x)) }
function clamp01(x) { return clamp(x, 0, 1) }
function lerp(a, b, t) { return a + (b - a) * t }

function smoothParams(out, a, b, t) {
  for (const k in a) out[k] = lerp(a[k], b[k], t)
  return out
}

function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0))
  return t * t * (3 - 2 * t)
}

function hexToRGB01(hex) {
  let h = (hex || '#000').replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const n = parseInt(h, 16) >>> 0
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image failed: ' + url))
    img.src = url
  })
}

/** Center-crop to a square <canvas>, capped at maxSquare px. */
function cropImageToSquareCanvas(img, maxSquare) {
  const iw = img.naturalWidth || img.width
  const ih = img.naturalHeight || img.height
  const crop = Math.min(iw, ih)
  const sx = ((iw - crop) * 0.5) | 0
  const sy = ((ih - crop) * 0.5) | 0
  const out = Math.min(crop, maxSquare)
  const canvas = document.createElement('canvas')
  canvas.width = out
  canvas.height = out
  canvas.getContext('2d', { willReadFrequently: true }).drawImage(img, sx, sy, crop, crop, 0, 0, out, out)
  return canvas
}

// Reusable 256² scratch canvas for histogram building (avoids a per-call alloc).
let _histScratch = null
function histScratch() {
  if (!_histScratch) {
    const cnv = document.createElement('canvas')
    cnv.width = 256
    cnv.height = 256
    _histScratch = { ctx: cnv.getContext('2d', { willReadFrequently: true }) }
  }
  return _histScratch
}

/**
 * Build the luminance histogram + dark/light classification for one (square)
 * image. This is the EXPENSIVE, scroll-INDEPENDENT half (drawImage + getImageData
 * + 65k-px loop) — run once per slot at upload, cached on this.slotHist[].
 */
function buildHistogram(img) {
  const size = 256
  const bins = 256
  const { ctx } = histScratch()

  const iw = img.naturalWidth || img.width
  const ih = img.naturalHeight || img.height
  const sc = Math.min(size / iw, size / ih)
  const dw = Math.max(1, Math.floor(iw * sc))
  const dh = Math.max(1, Math.floor(ih * sc))
  ctx.clearRect(0, 0, size, size)
  ctx.drawImage(img, Math.floor((size - dw) / 2), Math.floor((size - dh) / 2), dw, dh)
  const { data } = ctx.getImageData(0, 0, size, size)

  const hist = new Uint32Array(bins)
  let sumLum = 0, n = 0, darkCount = 0
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 5) continue
    const lum = 0.2126 * (data[i] / 255) + 0.7152 * (data[i + 1] / 255) + 0.0722 * (data[i + 2] / 255)
    hist[Math.min(bins - 1, Math.max(0, (lum * (bins - 1)) | 0))]++
    sumLum += lum
    n++
    if (lum < 0.5) darkCount++
  }

  const meanLum = n ? sumLum / n : 0.5
  const darkFrac = n ? darkCount / n : 0.5
  const isDark = meanLum < DARK_MEAN_CUT || darkFrac > DARK_FRAC_CUT
  return { hist, n, isDark, mean: meanLum }
}

/**
 * Derive the 3 key thresholds from a cached histogram + the current scroll params
 * (tail grows with scroll). This is the CHEAP, scroll-DEPENDENT half — two ~256-bin
 * percentile walks, no getImageData. Output is identical to the old combined fn.
 */
function deriveThresholds(stats, CUR) {
  const bins = 256
  const { hist, n, isDark } = stats

  const tailPct = clamp01(!isDark ? CUR.lightPct : 1.0 - CUR.darkPct)
  const p1 = tailPct / 3, p2 = (2 * tailPct) / 3, p3 = tailPct

  const fromBottom = (p) => {
    if (n <= 0) return 0
    const target = Math.max(1, Math.floor(p * n))
    let acc = 0
    for (let b = 0; b < bins; b++) { acc += hist[b]; if (acc >= target) return b }
    return bins - 1
  }
  const fromTop = (p) => {
    if (n <= 0) return bins - 1
    const target = Math.max(1, Math.floor(p * n))
    let acc = 0
    for (let b = bins - 1; b >= 0; b--) { acc += hist[b]; if (acc >= target) return b }
    return 0
  }

  let b1, b2, b3, mode
  if (!isDark) {
    mode = 0
    ;[b1, b2, b3] = [fromBottom(p1), fromBottom(p2), fromBottom(p3)].sort((a, b) => a - b)
  } else {
    mode = 1
    ;[b1, b2, b3] = [fromTop(p1), fromTop(p2), fromTop(p3)].sort((a, b) => b - a)
  }
  return { t1: b1 / (bins - 1), t2: b2 / (bins - 1), t3: b3 / (bins - 1), mode }
}
